import { useMemo, useState } from 'react';
import { EntityLink, RowLink, ROW_LINK_HOST } from '../components/links/EntityLink';
import { realLinkProps } from '../components/links/realLinkProps';
import { useRouterNavigate } from '../components/links/useEntityLinkProps';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  Card,
  CardHeader,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableCellLayout,
  Button,
  Checkbox,
} from '@fluentui/react-components';
import {
  OpenRegular,
  WarningRegular,
  CalendarClockRegular,
  BuildingRegular,
  TagMultipleRegular,
  ClipboardTaskListLtrRegular,
  AlertRegular,
  ChevronRightRegular,
  AlertSnoozeRegular,
  DismissCircleRegular,
  MoneyRegular,
} from '@fluentui/react-icons';
import { useDashboardStats } from '../services/complianceApi';
import {
  useStateTaxClients,
  useObligationTaxTypesByClient,
  useFilingHealthByClient,
  useClientOwedStatus,
  useActiveSnoozes,
  useSnoozeClient,
  useUnsnoozeClient,
  useClientBillingSummaries,
} from '../services/stateTaxClientsApi';
import type { ComplianceTask } from '../types';
import { daysUntil, formatWholeDay } from '../utils/complianceFormatters';
import { monthsAgo } from '../utils/dateMath';
import { expectedPeriodMonths, isTestClient } from '../utils/obligationHelpers';
import { trendColor, trendLabel } from '../utils/dashboardTrend';
import { apiErrorMessage } from '../lib/apiErrorMessage';
import SecondaryLoadWarning from '../components/common/SecondaryLoadWarning';
import MyPeriodCard from '../components/moments/MyPeriodCard';
import { owesNothingNow } from '../utils/needsAttention';
import { pageShell } from '../components/layout/pageShell';
import TaskStatusBadge from '../components/compliance/TaskStatusBadge';
import { appLocale } from '../utils/appLocale';

const useStyles = makeStyles({
  page: {
    ...pageShell,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  title: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '1.1',
    margin: 0,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },
  sectionCard: {
    padding: tokens.spacingVerticalM,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
  },
  overdue: { color: tokens.colorPaletteRedForeground1 },
  emptyRow: {
    padding: tokens.spacingVerticalL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  taxTypeBreakdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  breakdownRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    padding: `${tokens.spacingVerticalXS} 0`,
  },
  breakdownLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase300,
  },
  breakdownBar: {
    width: '100%',
    height: '6px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '3px',
    overflow: 'hidden',
    marginTop: '2px',
  },
  breakdownBarFill: {
    height: '100%',
    backgroundColor: tokens.colorBrandBackground,
    borderRadius: '3px',
    transition: 'width 200ms ease',
  },
  breakdownCount: {
    fontVariantNumeric: 'tabular-nums',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  // At-risk panel — clients sorted by recency of last filing.
  atRiskList: {
    display: 'flex',
    flexDirection: 'column',
    // Tight rows so the panel fits 8 entries comfortably without scrolling.
  },
  atRiskRow: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto auto auto auto',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    padding: `${tokens.spacingVerticalS} 0`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    transition: 'background-color 80ms ease-in',
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    marginLeft: `-${tokens.spacingHorizontalS}`,
    marginRight: `-${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalS,
  },
  bulkBarCount: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  atRiskRowFirst: {
    borderTop: 'none',
  },
  // A real link (fleet ruling 2026-10-29) that reads as the text it replaced: underlined on hover only.
  rowLinkText: {
    color: 'inherit',
    textDecorationLine: 'none',
    ':hover': { textDecorationLine: 'underline' },
  },
  atRiskName: {
    display: 'block',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  atRiskMeta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  atRiskNever: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
  },
  atRiskStale: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
  },
  atRiskChevron: {
    color: tokens.colorNeutralForeground3,
  },
  // Inline category badge on at-risk rows. Distinguishes "never filed" (red,
  // strongest signal — likely a new client awaiting paperwork) from "lapsed"
  // (amber — was filing, recently dropped off; different action than new client).
  atRiskCategory: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  atRiskCategoryNever: {
    color: tokens.colorPaletteRedForeground1,
    backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
  },
  atRiskCategoryLapsed: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    backgroundColor: tokens.colorPaletteDarkOrangeBackground1,
    border: `1px solid ${tokens.colorPaletteDarkOrangeBorder1}`,
  },
  // Snoozed list — appears under the at-risk panel when there's anything snoozed.
  // Compact row per client with an "X" affordance to unsnooze. Closes the loop:
  // staff snoozed something by mistake (or finished the follow-up) and needs to
  // bring it back without waiting for the snooze to expire.
  snoozedSection: {
    marginTop: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  snoozedHeader: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    marginBottom: tokens.spacingVerticalS,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  snoozedRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto auto',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    padding: `4px 0`,
    fontSize: tokens.fontSizeBase200,
  },
  snoozedName: {
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  snoozedUntil: {
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: 'tabular-nums',
  },
  snoozedReason: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

type FluentBadgeColor = 'brand' | 'severe' | 'warning' | 'success' | 'informative' | 'subtle' | 'danger' | 'important';
function badgeColorForTaxType(taxType: string): FluentBadgeColor {
  const t = taxType.toLowerCase();
  if (t.includes('sales tax')) return 'brand';
  if (t.includes('documentary stamp') || t.includes('doc stamp')) return 'severe';
  if (t.includes('solid waste')) return 'warning';
  if (t.includes('communication')) return 'informative';
  if (t.includes('tangible')) return 'success';
  if (t.includes('gross receipts')) return 'important';
  if (t.includes('fuel')) return 'danger';
  return 'subtle';
}

function shortTaxLabel(taxType: string): string {
  if (/sales\s*tax/i.test(taxType)) return 'Sales Tax';
  if (/documentary\s*stamp|doc\s*stamp/i.test(taxType)) return 'Doc Stamp';
  if (/solid\s*waste/i.test(taxType)) return 'Solid Waste';
  if (/communication/i.test(taxType)) return 'Comm Services';
  if (/tangible/i.test(taxType)) return 'TPP';
  if (/gross\s*receipts/i.test(taxType)) return 'Gross Receipts';
  if (/fuel/i.test(taxType)) return 'Fuel';
  return taxType;
}

// A filing marked filed with a future bank-debit date is scheduled but not yet debited.
const scheduledDebit = (task: ComplianceTask): string | null => {
  const future = task.filings
    .map((f) => f.bankDebitDate)
    .filter((d): d is string => !!d && Date.parse(d) > Date.now())
    .sort()[0];
  return future ?? null;
};

function TaskListTable({ tasks, overdueStyling, emptyText }: { tasks: ComplianceTask[]; overdueStyling: boolean; emptyText: string }) {
  const styles = useStyles();
  const linkTo = useRouterNavigate();
  const { t: tr } = useTranslation();

  if (tasks.length === 0) {
    return <div className={styles.emptyRow}><Text>{emptyText}</Text></div>;
  }

  return (
    <Table size="small" aria-label={tr('dashboardPage.table.ariaLabel')}>
      <TableHeader>
        <TableRow>
          <TableHeaderCell>{tr('dashboardPage.table.client')}</TableHeaderCell>
          <TableHeaderCell>{tr('dashboardPage.table.period')}</TableHeaderCell>
          <TableHeaderCell>{tr('dashboardPage.table.due')}</TableHeaderCell>
          <TableHeaderCell>{tr('dashboardPage.table.status')}</TableHeaderCell>
          <TableHeaderCell></TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {tasks.map((t) => {
          const days = daysUntil(t.dueDate);
          const debit = scheduledDebit(t);
          return (
            <TableRow key={t.id}>
              <TableCell><TableCellLayout>{t.clientName}</TableCellLayout></TableCell>
              <TableCell><TableCellLayout>{t.periodLabel}</TableCellLayout></TableCell>
              <TableCell>
                <TableCellLayout
                  description={
                    overdueStyling
                      ? tr('dashboardPage.daysOverdue', { count: Math.abs(days) })
                      : tr('dashboardPage.inDays', { count: days })
                  }
                >
                  <span className={overdueStyling ? styles.overdue : undefined}>
                    {formatWholeDay(t.dueDate)}
                  </span>
                </TableCellLayout>
              </TableCell>
              <TableCell>
                <TableCellLayout>
                  <TaskStatusBadge task={t} />
                  {debit && (
                    <Badge appearance="tint" color="informative" size="small">
                      {tr('dashboardPage.scheduledToBePaid', { date: formatWholeDay(debit, { year: 'numeric', month: 'numeric', day: 'numeric' }) })}
                    </Badge>
                  )}
                </TableCellLayout>
              </TableCell>
              <TableCell>
                <Button
                  as="a"
                  appearance="subtle"
                  icon={<OpenRegular />}
                  size="small"
                  {...realLinkProps(`/compliance/tasks/${t.id}?clientId=${encodeURIComponent(t.clientId)}`, { navigate: linkTo })}
                >
                  {tr('tasksPage.open')}
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}


// Same frequency-aware staleness as the clients list — duplicated rather than
// extracted to a shared util module since both pages use only the helper they
// need; if a third caller appears, lift to src/utils/freshness.ts.

// Snooze duration. Server clamps to [1, 365] but this is the dashboard's default.
const SNOOZE_DAYS = 30;

// Filed-tile W-o-W trend subtitle. Surfaces direction + magnitude so staff can
// spot a slowdown without scrolling; intentionally small (11px) since the main
// stat-value already carries the headline number.

export default function ComplianceDashboardPage() {
  const styles = useStyles();
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useDashboardStats();
  const { data: clients, isError: clientsError } = useStateTaxClients();
  const { data: billingSummaries, isError: billingError } = useClientBillingSummaries();

  // Clients in bad standing — those with overdue invoices, joined to names, worst-first.
  // Also tally clients whose recurring billing has an obligation gap. Test fixtures
  // (Test Company) are excluded like everywhere else on this page.
  const badStanding = useMemo(() => {
    if (!billingSummaries) return [];
    const nameById = new Map((clients ?? []).map((c) => [c.stateTaxClientId, c.accountName]));
    return billingSummaries
      .filter((b) => b.overdue.count > 0)
      .filter((b) => !isTestClient(nameById.get(b.stateTaxClientId)))
      .map((b) => ({ billing: b, name: nameById.get(b.stateTaxClientId) ?? '(unknown)' }))
      .sort((a, b) => b.billing.overdue.oldestDaysOverdue - a.billing.overdue.oldestDaysOverdue);
  }, [billingSummaries, clients]);

  const billingGapCount = useMemo(
    () => (billingSummaries ?? []).filter((b) => b.reconciliation.status === 'gaps').length,
    [billingSummaries],
  );
  const { data: taxTypesByClient, isError: taxTypesError } = useObligationTaxTypesByClient();
  const { data: filingHealth, isError: filingHealthError } = useFilingHealthByClient();
  // FB-2: the obligation lifecycle. A failed or empty read keeps every client on the list (fail-safe).
  const { data: owedStatus } = useClientOwedStatus();

  // Snoozes are Cosmos-backed (clientSnoozes container) so all staff see the
  // same hidden-from-triage list. React Query handles freshness; the mutation
  // invalidates the active-snoozes cache on success.
  const { accounts } = useMsal();
  const activeAccount = accounts[0];
  const { data: activeSnoozes, isError: snoozesError } = useActiveSnoozes();
  const snoozeMutation = useSnoozeClient();
  const unsnoozeMutation = useUnsnoozeClient();

  // Indexed view of the snoozes for O(1) "is this client snoozed" checks during
  // the at-risk filter pass below. Server already filters expired so we don't
  // re-check here.
  const snoozedIds = useMemo(() => {
    const set = new Set<string>();
    for (const s of activeSnoozes ?? []) set.add(s.stateTaxClientId);
    return set;
  }, [activeSnoozes]);

  function snoozeClient(stateTaxClientId: string, clientName: string) {
    // Optional reason — staff often has a "why" (waiting on FL DOR, client on
    // hold, etc.). The server-side ClientSnooze model has had a reason field
    // since the slice shipped; this just wires the UI side. Empty string and
    // null are equivalent on the wire — server treats both as no reason.
    const reasonRaw = window.prompt(
      t('dashboardPage.atRisk.snoozePrompt', { name: clientName, days: SNOOZE_DAYS }),
      '',
    );
    if (reasonRaw === null) return; // user cancelled
    snoozeMutation.mutate({
      stateTaxClientId,
      payload: {
        days: SNOOZE_DAYS,
        createdByName: activeAccount?.name ?? activeAccount?.username,
        createdById: activeAccount?.localAccountId,
        reason: reasonRaw.trim() || undefined,
      },
    });
  }

  // Multi-select for batch-snooze on the at-risk panel. Common end-of-triage flow:
  // staff reviews the list, dismisses several at once with a shared reason ("waiting
  // on Q1 docs", "all migrated to direct portal access this week"). Per-client reason
  // path (snoozeClient above) stays available via the row's individual snooze button.
  const [selectedAtRiskIds, setSelectedAtRiskIds] = useState<Set<string>>(new Set());

  function toggleSelected(id: string, checked: boolean) {
    setSelectedAtRiskIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function bulkSnooze() {
    if (selectedAtRiskIds.size === 0) return;
    const reasonRaw = window.prompt(
      t('dashboardPage.atRisk.bulkSnoozePrompt', { count: selectedAtRiskIds.size, days: SNOOZE_DAYS }),
      '',
    );
    if (reasonRaw === null) return;
    const ids = Array.from(selectedAtRiskIds);
    const reason = reasonRaw.trim() || undefined;
    // Fire mutations in parallel; React Query dedups the active-snoozes
    // invalidation, so the panel re-renders once when all settle. Errors on a
    // subset are tolerated — successful ones still snooze; staff sees the
    // remaining (failed) clients still in the list and can retry.
    await Promise.allSettled(
      ids.map((id) =>
        snoozeMutation.mutateAsync({
          stateTaxClientId: id,
          payload: {
            days: SNOOZE_DAYS,
            createdByName: activeAccount?.name ?? activeAccount?.username,
            createdById: activeAccount?.localAccountId,
            reason,
          },
        }),
      ),
    );
    setSelectedAtRiskIds(new Set());
  }

  // Top at-risk clients: never-filed + clients past 2× their expected filing period
  // (frequency-aware — a Quarterly client who filed last quarter is on time, not
  // stale). Test/placeholder accounts AND snoozed clients are excluded so triage
  // stays focused on what actually needs follow-up. Capped at 8 rows.
  const atRiskClients = useMemo(() => {
    if (!clients || !filingHealth) return [];
    return clients
      .filter((c) => !isTestClient(c.accountName))
      .filter((c) => !snoozedIds.has(c.stateTaxClientId))
      // FB-2: a client whose obligations are all cancelled, or all ended with the closing period filed, owes nothing.
      .filter((c) => !owesNothingNow(owedStatus?.[c.stateTaxClientId], filingHealth[c.stateTaxClientId]?.lastFiledMonth))
      .map((c) => {
        const h = filingHealth[c.stateTaxClientId];
        const months = h?.lastFiledMonth ? monthsAgo(h.lastFiledMonth) : Number.MAX_SAFE_INTEGER;
        const period = expectedPeriodMonths(c.filingFrequencyLabel);
        return { client: c, months, period, lastFiled: h?.lastFiledMonth ?? null };
      })
      // Stale = past 2× the cadence. Monthly: 2+ months. Quarterly: 4+ months.
      // Annual: 13+ months. Plus everyone we've never seen a filing from.
      .filter((row) => row.months > row.period * 2)
      .sort((a, b) => b.months - a.months)
      .slice(0, 8);
  }, [clients, filingHealth, snoozedIds, owedStatus]);

  // Snoozed-clients list for the panel under the at-risk section. Joins the
  // active-snooze rows with the client list to surface the human-readable name.
  // Sorted by snooze expiry ascending so the soonest-to-return shows first.
  const snoozedClients = useMemo(() => {
    if (!clients || !activeSnoozes) return [];
    const byId = new Map(clients.map(c => [c.stateTaxClientId, c]));
    return activeSnoozes
      .map(s => ({
        snooze: s,
        client: byId.get(s.stateTaxClientId),
      }))
      .filter(r => r.client !== undefined)
      .sort((a, b) => a.snooze.snoozeUntil.localeCompare(b.snooze.snoozeUntil));
  }, [clients, activeSnoozes]);

  // Derived stats from the Dataverse-backed obligation map. These work today even though
  // the Cosmos compliance task pipeline is empty — they don't depend on the task generator.
  // Test fixtures (Test Company) are excluded so the tiles agree with the clients list,
  // which hides them post-rollout.
  const obligationStats = useMemo(() => {
    if (!clients || !taxTypesByClient) return null;
    const testIds = new Set(
      clients.filter((c) => isTestClient(c.accountName)).map((c) => c.stateTaxClientId),
    );
    const obligationEntries = Object.entries(taxTypesByClient).filter(([id]) => !testIds.has(id));
    const totalClients = clients.length - testIds.size;
    const clientsWithObligations = obligationEntries.length;
    const multiObligationClients = obligationEntries.filter(([, arr]) => arr.length > 1).length;
    const taxTypeCounts = new Map<string, number>();
    for (const [, arr] of obligationEntries) {
      for (const tt of arr) taxTypeCounts.set(tt, (taxTypeCounts.get(tt) ?? 0) + 1);
    }
    const breakdown = Array.from(taxTypeCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([taxType, count]) => ({ taxType, count }));
    const totalObligations = breakdown.reduce((s, b) => s + b.count, 0);
    return {
      totalClients,
      clientsWithObligations,
      multiObligationClients,
      breakdown,
      totalObligations,
      maxBreakdown: breakdown[0]?.count ?? 0,
    };
  }, [clients, taxTypesByClient]);

  const totalOpen =
    (data?.statusCounts.pending ?? 0) + (data?.statusCounts.in_progress ?? 0);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Text as="h1" className={styles.title}>{t('dashboardPage.title')}</Text>
        <Text className={styles.subtitle}>{t('dashboardPage.subtitle')}</Text>
      </div>

      {isError && (
        <MessageBar intent="error">
          <MessageBarBody>
            {apiErrorMessage(error, t, 'dashboardPage.loadError')}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* STX-PROD-05: "Needs attention" and the tiles read empty or zero when these fail; say which ones did. */}
      {!isError && (
        <SecondaryLoadWarning
          failed={[
            clientsError && 'clients', billingError && 'billing', taxTypesError && 'taxTypes',
            filingHealthError && 'filingHealth', snoozesError && 'snoozes',
          ]}
        />
      )}

      {/* Personal, never comparative: MY period ring + MY overdue state; fires the period-complete
          moment when the ring closes (accomplishment moments, spec 2026-10-20). */}
      <MyPeriodCard />

      {/* Obligation overview — Dataverse-backed, works whether or not Cosmos task data exists. */}
      {obligationStats && (
        <div className="stat-grid">
          <div className="card">
            <div className="stat-value stat-value--neutral">{obligationStats.totalClients}</div>
            <div className="stat-label">{t('dashboardPage.obligationStats.activeClients')}</div>
          </div>
          <div className="card">
            <div className="stat-value">{obligationStats.totalObligations}</div>
            <div className="stat-label">{t('dashboardPage.obligationStats.trackedObligations')}</div>
          </div>
          <div className="card">
            <div className="stat-value stat-value--success">{obligationStats.multiObligationClients}</div>
            <div className="stat-label">{t('dashboardPage.obligationStats.multiObligationClients')}</div>
          </div>
          <div className="card">
            <div
              className={
                (data?.overdueTasks.length ?? 0) > 0
                  ? 'stat-value stat-value--warning'
                  : 'stat-value stat-value--neutral'
              }
            >
              {data?.overdueTasks.length ?? 0}
            </div>
            <div className="stat-label">{t('dashboardPage.stats.overdue')}</div>
          </div>
        </div>
      )}

      {atRiskClients.length > 0 && (
        <Card className={styles.sectionCard}>
          <CardHeader
            header={
              <div className={styles.sectionTitle}>
                <AlertRegular style={{ color: 'var(--colorPaletteRedForeground1, #c42b1c)' }} />
                <span>{t('dashboardPage.atRisk.title')}</span>
                <Badge appearance="tint" color="danger">{atRiskClients.length}</Badge>
              </div>
            }
            description={
              <Text className={styles.atRiskMeta}>
                {t('dashboardPage.atRisk.description', { days: SNOOZE_DAYS })}
              </Text>
            }
          />
          {selectedAtRiskIds.size > 0 && (
            <div className={styles.bulkBar}>
              <span className={styles.bulkBarCount}>
                {t('dashboardPage.atRisk.selectedCount', { count: selectedAtRiskIds.size })}
              </span>
              <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
                <Button
                  appearance="subtle"
                  size="small"
                  onClick={() => setSelectedAtRiskIds(new Set())}
                >
                  {t('dashboardPage.atRisk.clearSelection')}
                </Button>
                <Button
                  appearance="primary"
                  size="small"
                  icon={<AlertSnoozeRegular />}
                  disabled={snoozeMutation.isPending}
                  onClick={() => { void bulkSnooze(); }}
                >
                  {t('dashboardPage.atRisk.bulkSnoozeButton', { count: selectedAtRiskIds.size, days: SNOOZE_DAYS })}
                </Button>
              </div>
            </div>
          )}
          <div className={styles.atRiskList}>
            {atRiskClients.map((row, idx) => {
              const lastLabel = row.lastFiled
                ? formatWholeDay(row.lastFiled, { month: 'short', year: 'numeric' })
                : null;
              const isNever = !row.lastFiled;
              return (
                <div
                  key={row.client.stateTaxClientId}
                  className={`${styles.atRiskRow} ${idx === 0 ? styles.atRiskRowFirst : ''} ${ROW_LINK_HOST}`}
                >
                  <Checkbox
                    checked={selectedAtRiskIds.has(row.client.stateTaxClientId)}
                    onChange={(_e, data) => toggleSelected(row.client.stateTaxClientId, !!data.checked)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    aria-label={t('dashboardPage.atRisk.selectAria', { name: row.client.accountName })}
                  />
                  <div>
                    <RowLink href={`/clients/${row.client.stateTaxClientId}`} className={mergeClasses(styles.rowLinkText, styles.atRiskName)}>
                      {row.client.accountName || '(unnamed)'}
                    </RowLink>
                    {row.client.ownerName && (
                      <div className={styles.atRiskMeta}>{row.client.ownerName}</div>
                    )}
                  </div>
                  <span className={`${styles.atRiskCategory} ${isNever ? styles.atRiskCategoryNever : styles.atRiskCategoryLapsed}`}
                    title={isNever
                      ? t('dashboardPage.atRisk.categoryNeverFiledTooltip')
                      : t('dashboardPage.atRisk.categoryLapsedTooltip', { lastLabel })}>
                    {isNever
                      ? t('dashboardPage.atRisk.categoryNeverFiled')
                      : t('dashboardPage.atRisk.categoryLapsed')}
                  </span>
                  {/* "Last: <month>" only renders for the lapsed case — the "Never filed"
                      badge above already conveys when there's nothing on record, so a
                      redundant "Never" text would just add visual noise. Empty placeholder
                      keeps the grid column count consistent across both row variants. */}
                  {lastLabel
                    ? <span className={styles.atRiskStale}>{t('dashboardPage.atRisk.lastFiled', { date: lastLabel })}</span>
                    : <span aria-hidden="true" />}
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<AlertSnoozeRegular />}
                    onClick={(e) => {
                      e.stopPropagation();
                      snoozeClient(row.client.stateTaxClientId, row.client.accountName);
                    }}
                    title={t('dashboardPage.atRisk.snoozeTitle', { days: SNOOZE_DAYS })}
                    aria-label={t('dashboardPage.atRisk.snoozeAria', { name: row.client.accountName, days: SNOOZE_DAYS })}
                  />
                  <ChevronRightRegular className={styles.atRiskChevron} />
                </div>
              );
            })}
          </div>

          {snoozedClients.length > 0 && (
            <div className={styles.snoozedSection}>
              <div className={styles.snoozedHeader}>
                {t('dashboardPage.atRisk.snoozedHeader', { count: snoozedClients.length })}
              </div>
              {snoozedClients.map((row) => {
                const untilDate = new Date(row.snooze.snoozeUntil);
                const untilLabel = untilDate.toLocaleDateString(appLocale(), { month: 'short', day: 'numeric' });
                const c = row.client!;
                const reason = row.snooze.reason?.trim();
                return (
                  <div key={row.snooze.id} className={styles.snoozedRow}>
                    <EntityLink
                      href={`/clients/${c.stateTaxClientId}`}
                      className={mergeClasses(styles.rowLinkText, styles.snoozedName)}
                      title={reason ? `${c.accountName} — ${reason}` : c.accountName}
                    >
                      {c.accountName || '(unnamed)'}
                      {reason && (
                        <span className={styles.snoozedReason}> · {reason}</span>
                      )}
                    </EntityLink>
                    <span className={styles.snoozedUntil}>
                      {t('dashboardPage.atRisk.snoozedUntil', { date: untilLabel })}
                    </span>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<DismissCircleRegular />}
                      onClick={() => unsnoozeMutation.mutate(c.stateTaxClientId)}
                      disabled={unsnoozeMutation.isPending}
                      title={t('dashboardPage.atRisk.unsnoozeTitle')}
                      aria-label={t('dashboardPage.atRisk.unsnoozeAria', { name: c.accountName })}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {badStanding.length > 0 && (
        <Card className={styles.sectionCard}>
          <CardHeader
            header={
              <div className={styles.sectionTitle}>
                <MoneyRegular />
                <span>{t('dashboardPage.badStanding.title')}</span>
                <Badge appearance="tint" color="danger">{badStanding.length}</Badge>
                {billingGapCount > 0 && (
                  <Badge appearance="tint" color="warning" title={t('dashboardPage.badStanding.gapsTooltip')}>
                    {t('dashboardPage.badStanding.gaps', { count: billingGapCount })}
                  </Badge>
                )}
              </div>
            }
            description={
              <Text className={styles.atRiskMeta}>{t('dashboardPage.badStanding.description')}</Text>
            }
          />
          <Table size="small" aria-label={t('dashboardPage.badStanding.title')}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t('dashboardPage.badStanding.cols.client')}</TableHeaderCell>
                <TableHeaderCell>{t('dashboardPage.badStanding.cols.overdue')}</TableHeaderCell>
                <TableHeaderCell>{t('dashboardPage.badStanding.cols.oldest')}</TableHeaderCell>
                <TableHeaderCell>{t('dashboardPage.badStanding.cols.amount')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {badStanding.map(({ billing, name }) => (
                <TableRow
                  key={billing.stateTaxClientId}
                  className={ROW_LINK_HOST}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <TableCellLayout>
                      <RowLink href={`/clients/${billing.stateTaxClientId}`} className={styles.rowLinkText}>{name}</RowLink>
                    </TableCellLayout>
                  </TableCell>
                  <TableCell><TableCellLayout>{billing.overdue.count}</TableCellLayout></TableCell>
                  <TableCell>
                    <TableCellLayout>
                      {t('dashboardPage.badStanding.daysOverdue', { count: billing.overdue.oldestDaysOverdue })}
                    </TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>${billing.overdue.totalAmountDue.toFixed(2)}</TableCellLayout>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <div className={styles.twoCol}>
        {/* Tax-type breakdown card — shows distribution of obligations across the client base. */}
        {obligationStats && obligationStats.breakdown.length > 0 && (
          <Card className={styles.sectionCard}>
            <CardHeader
              header={
                <div className={styles.sectionTitle}>
                  <TagMultipleRegular />
                  <span>{t('dashboardPage.sections.byTaxType')}</span>
                </div>
              }
            />
            <div className={styles.taxTypeBreakdown}>
              {obligationStats.breakdown.map((b) => (
                <div key={b.taxType} className={styles.breakdownRow}>
                  <div>
                    <div className={styles.breakdownLabel}>
                      <Badge appearance="tint" color={badgeColorForTaxType(b.taxType)} size="small">
                        {shortTaxLabel(b.taxType)}
                      </Badge>
                    </div>
                    <div className={styles.breakdownBar}>
                      <div
                        className={styles.breakdownBarFill}
                        style={{ width: `${(b.count / obligationStats.maxBreakdown) * 100}%` }}
                      />
                    </div>
                  </div>
                  <span className={styles.breakdownCount}>{b.count}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Client onboarding hint when client list is loaded but obligations map isn't yet. */}
        {!obligationStats && (
          <Card className={styles.sectionCard}>
            <CardHeader
              header={
                <div className={styles.sectionTitle}>
                  <BuildingRegular />
                  <span>{t('dashboardPage.sections.loadingObligations')}</span>
                </div>
              }
            />
            <div className={styles.emptyRow}>
              <Spinner size="small" />
            </div>
          </Card>
        )}

        {/* Compliance task generator status. The Cosmos task pipeline is empty in dev;
            the Dataverse-side data isn't yet wired into the task generator. Until then,
            this card explains the current state cleanly instead of showing zeros. */}
        <Card className={styles.sectionCard}>
          <CardHeader
            header={
              <div className={styles.sectionTitle}>
                <ClipboardTaskListLtrRegular />
                <span>{t('dashboardPage.sections.pipeline')}</span>
              </div>
            }
          />
          {isLoading ? (
            <div className={styles.emptyRow}><Spinner size="small" /></div>
          ) : data && (data.dueSoonTasks.length > 0 || data.overdueTasks.length > 0 || totalOpen > 0) ? (
            <div className="stat-grid" style={{ gap: '12px' }}>
              <div>
                <div className="stat-value stat-value--neutral" style={{ fontSize: tokens.fontSizeBase600 }}>{totalOpen}</div>
                <div className="stat-label">{t('dashboardPage.stats.openTasks')}</div>
              </div>
              <div>
                <div className="stat-value" style={{ fontSize: tokens.fontSizeBase600 }}>{data.dueSoonTasks.length}</div>
                <div className="stat-label">{t('dashboardPage.stats.dueIn7Days')}</div>
              </div>
              <div>
                <div className="stat-value stat-value--success" style={{ fontSize: tokens.fontSizeBase600 }}>
                  {(data.statusCounts.filed ?? 0) + (data.statusCounts.client_notified ?? 0)}
                </div>
                <div className="stat-label">{t('dashboardPage.stats.filed')}</div>
                {data.filingsTrend && (
                  <div
                    className="stat-trend"
                    style={{
                      fontSize: tokens.fontSizeBase200,
                      color: trendColor(data.filingsTrend.thisWeek, data.filingsTrend.lastWeek),
                      marginTop: '2px',
                    }}
                  >
                    {trendLabel(data.filingsTrend.thisWeek, data.filingsTrend.lastWeek, t)}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.emptyRow}>
              <Text block style={{ marginBottom: '8px' }}>
                {t('dashboardPage.pipelineEmpty.schedule')}
              </Text>
              <Text block style={{ fontSize: tokens.fontSizeBase200 }}>
                {t('dashboardPage.pipelineEmpty.migration')}
              </Text>
            </div>
          )}
        </Card>
      </div>

      {data && (data.overdueTasks.length > 0 || data.dueSoonTasks.length > 0) && (
        <div className={styles.twoCol}>
          <Card className={styles.sectionCard}>
            <CardHeader
              header={
                <div className={styles.sectionTitle}>
                  <WarningRegular />
                  <span>{t('dashboardPage.sections.overdue')}</span>
                  <Badge appearance="tint" color="danger">{data.overdueTasks.length}</Badge>
                </div>
              }
            />
            <TaskListTable tasks={data.overdueTasks} overdueStyling emptyText={t('dashboardPage.empty.noneOverdue')} />
          </Card>

          <Card className={styles.sectionCard}>
            <CardHeader
              header={
                <div className={styles.sectionTitle}>
                  <CalendarClockRegular />
                  <span>{t('dashboardPage.sections.dueSoon')}</span>
                  <Badge appearance="tint" color="informative">{data.dueSoonTasks.length}</Badge>
                </div>
              }
            />
            <TaskListTable tasks={data.dueSoonTasks} overdueStyling={false} emptyText={t('dashboardPage.empty.noneDueSoon')} />
          </Card>
        </div>
      )}

      {data && data.workload.length > 0 && (
        <Card className={styles.sectionCard}>
          <CardHeader header={<div className={styles.sectionTitle}>{t('dashboardPage.sections.preparerWorkload')}</div>} />
          <Table size="small" aria-label={t('dashboardPage.sections.preparerWorkload')}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t('dashboardPage.table.preparer')}</TableHeaderCell>
                <TableHeaderCell>{t('dashboardPage.table.openTasks')}</TableHeaderCell>
                <TableHeaderCell>{t('dashboardPage.table.openFilings')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.workload.map((w) => (
                <TableRow key={w.preparerName}>
                  <TableCell><TableCellLayout>{w.preparerName}</TableCellLayout></TableCell>
                  <TableCell><TableCellLayout>{w.openTasks}</TableCellLayout></TableCell>
                  <TableCell><TableCellLayout>{w.openFilings}</TableCellLayout></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
